import { useSetToast } from "@app/components/Toast";
import {
    MarkdownText,
    type MarkdownTextProps,
} from "@app/shared/components/MarkdownText/MarkdownText";
import { Badge } from "@design-system/DataDisplay/Badge";
import { DetailChip } from "@design-system/DataDisplay/DetailChip";
import {
    StatusTagCircular,
    type TagStatus,
} from "@design-system/DataDisplay/StatusTagCircular";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button, type ButtonProps } from "@design-system/Inputs/Button";
import { Switch } from "@design-system/Inputs/Switch";
import { FlexCol, FlexRow, type FlexProps } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text, type TextProps } from "@design-system/Typography/Text";
import { useMemo, type FC, type Ref } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { MultiSelectSection } from "./MultiSelectSection";
import { SingleSelectSection } from "./SingleSelectSection";
import { useQuestionTranslations } from "./i18n";

const QuestionCardHeader: FC<FlexProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    ...props
}) => {
    return (
        <FlexRow
            justifyContent="between"
            alignItems="center"
            ref={ref}
            {...props}
        />
    );
};

const QuestionCardStatus: FC<
    FlexProps & { status: TagStatus } & { ref?: Ref<HTMLDivElement> }
> = ({ status, ref, ...props }) => {
    const { t, keys } = useQuestionTranslations();
    return (
        <FlexRow
            justifyContent="start"
            alignItems="center"
            gap="2"
            ref={ref}
            {...props}
        >
            <StatusTagCircular status={status} size="lg" />
            <Text variant="body-sm" color="primary">
                {t(keys.status, { context: status })}
            </Text>
        </FlexRow>
    );
};

const QuestionCardLockedStatus: FC<TextProps> = ({ className, ...props }) => {
    const { t, keys } = useQuestionTranslations();
    return (
        <FlexRow alignItems="center" gap="1.5" className={className}>
            <Icon name="lock" size="sm" className="text-tertiary" />
            <Text variant="body-sm" color="tertiary" {...props}>
                {t(keys.locked)}
            </Text>
        </FlexRow>
    );
};

const QuestionCardProofWarning: FC<
    FlexProps & { ref?: Ref<HTMLDivElement> }
> = ({ ref, ...props }) => {
    const { t, keys } = useQuestionTranslations();

    return (
        <Badge
            ref={ref}
            content={t(keys.evidence_files_missing)}
            variant="warning"
            {...props}
        />
    );
};

const QuestionCardContent: FC<FlexProps & { ref?: Ref<HTMLDivElement> }> = ({
    ref,
    ...props
}) => {
    return <FlexCol w="full" gap="4" pt="0.5" ref={ref} {...props} />;
};

const QuestionCardTitle: FC<
    TextProps & { ref?: Ref<HTMLDivElement>; isRequired?: boolean }
> = ({ ref, isRequired, ...props }) => {
    return (
        <FlexRow>
            {isRequired && (
                <Text color="danger" variant="body-bold">
                    *
                </Text>
            )}
            <Text as="h3" variant="body-lg-bold" ref={ref} {...props} />
        </FlexRow>
    );
};

const QuestionCardDescription: FC<
    MarkdownTextProps & { ref?: Ref<HTMLDivElement> }
> = (props) => {
    return <MarkdownText className="text-secondary text-sm" {...props} />;
};

const QuestionCardGuidance: FC<
    TextProps & {
        frameworkGuidance?: string;
        expertiseGuidance?: string;
        questionnaireName?: string;
    }
> = ({ frameworkGuidance, expertiseGuidance, questionnaireName }) => {
    const { t, keys } = useQuestionTranslations();

    const frameworkGuidanceLength = frameworkGuidance?.length ?? 0;
    const expertiseGuidanceLength = expertiseGuidance?.length ?? 0;
    const tooLongGuidance =
        frameworkGuidanceLength + expertiseGuidanceLength > 400;
    const shouldShowFrameworkGuidance =
        !tooLongGuidance || expertiseGuidanceLength === 0;

    const displayedExpertiseGuidance = useMemo(() => {
        return (
            expertiseGuidance
                ?.slice(0, 400)
                .concat(
                    tooLongGuidance && !shouldShowFrameworkGuidance
                        ? "..."
                        : "",
                ) || ""
        );
    }, [expertiseGuidance, tooLongGuidance, shouldShowFrameworkGuidance]);

    const displayedFrameworkGuidance = useMemo(() => {
        return (
            frameworkGuidance
                ?.slice(0, 400)
                .concat(tooLongGuidance ? "..." : "") || ""
        );
    }, [frameworkGuidance, tooLongGuidance]);

    return (
        <FlexCol className="text-sm w-full">
            <FlexCol gap="4">
                {expertiseGuidanceLength > 0 && (
                    <FlexCol gap="1">
                        <FlexRow alignItems="center" gap="2">
                            <Text variant="body-sm-bold">
                                {t(keys.expertise_guidance)}
                            </Text>
                            <DetailChip>
                                <DetailChip.Icon name="logo" />
                            </DetailChip>
                        </FlexRow>
                        <MarkdownText className="text-secondary text-sm">
                            {displayedExpertiseGuidance}
                        </MarkdownText>
                    </FlexCol>
                )}
                {shouldShowFrameworkGuidance && frameworkGuidanceLength > 0 && (
                    <FlexCol gap="1">
                        <Text variant="body-sm-bold">
                            {t(keys.framework_guidance, {
                                frameworkName: questionnaireName,
                            })}
                        </Text>

                        <MarkdownText className="text-secondary text-sm">
                            {displayedFrameworkGuidance}
                        </MarkdownText>
                    </FlexCol>
                )}
            </FlexCol>
            {tooLongGuidance && (
                <Modal>
                    <Modal.Trigger asChild>
                        <FlexRow justifyContent="end">
                            <Button
                                variant="text"
                                size="sm"
                                className="text-beavrGreen"
                            >
                                {t(keys.view_full_guidance)}
                                <Button.Icon name="circleHelp" />
                            </Button>
                        </FlexRow>
                    </Modal.Trigger>
                    <Modal.Overlay scrollable centerContent>
                        <Modal.Content>
                            <Modal.Header withClose>
                                <Modal.Title>
                                    {t(keys.full_guidance_title)}
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body className="p-6 max-h-[600px] overflow-y-auto">
                                <FlexCol gap="4">
                                    {expertiseGuidanceLength > 0 && (
                                        <FlexCol gap="1">
                                            <Text variant="body-sm-bold">
                                                {t(keys.expertise_guidance)}
                                            </Text>
                                            <ReactMarkdown
                                                className="markdown text-secondary text-sm"
                                                remarkPlugins={[
                                                    remarkGfm,
                                                    remarkBreaks,
                                                ]}
                                            >
                                                {expertiseGuidance || ""}
                                            </ReactMarkdown>
                                        </FlexCol>
                                    )}
                                    {frameworkGuidanceLength > 0 && (
                                        <FlexCol gap="1">
                                            <Text variant="body-sm-bold">
                                                {t(keys.framework_guidance, {
                                                    frameworkName:
                                                        questionnaireName,
                                                })}
                                            </Text>
                                            <ReactMarkdown
                                                className="markdown text-secondary text-sm"
                                                remarkPlugins={[
                                                    remarkGfm,
                                                    remarkBreaks,
                                                ]}
                                            >
                                                {frameworkGuidance || ""}
                                            </ReactMarkdown>
                                        </FlexCol>
                                    )}
                                </FlexCol>
                            </Modal.Body>
                        </Modal.Content>
                    </Modal.Overlay>
                </Modal>
            )}
        </FlexCol>
    );
};

const QuestionCardSwitch: FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    label?: string;
}> = ({ checked, onChange, disabled, label }) => {
    return (
        <FlexCol w="full" alignItems="start">
            <Switch
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                label={label}
                labelPosition="right"
            />
        </FlexCol>
    );
};

const QuestionCardCopyButton: FC<
    Omit<ButtonProps, "onClick" | "children"> & { answer: string | undefined }
> = ({ answer, ...props }) => {
    const { t, keys } = useQuestionTranslations();
    const { setToastSuccess } = useSetToast();

    const copyToClipboard = () => {
        if (answer) {
            navigator.clipboard.writeText(answer);
            setToastSuccess(t(keys.copied_to_clipboard));
        }
    };
    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <Button
                    variant="outlined"
                    size="sm"
                    className="w-fit"
                    onClick={copyToClipboard}
                    {...props}
                >
                    <Button.Icon name="duplicate" />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{t(keys.copy)}</Tooltip.Content>
        </Tooltip>
    );
};

const QuestionCardNoteButton: FC<Omit<ButtonProps, "children">> = (props) => {
    const { t, keys } = useQuestionTranslations();

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <Button
                    variant="outlined"
                    size="sm"
                    className="w-fit"
                    {...props}
                >
                    <Button.Icon name="commentPlus" />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{t(keys.show_comment_tooltip)}</Tooltip.Content>
        </Tooltip>
    );
};

const QuestionCardAiSuggestionButton: FC<Omit<ButtonProps, "children">> = (
    props,
) => {
    const { t, keys } = useQuestionTranslations();

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <Button
                    variant="flatAccent"
                    size="sm"
                    className="w-fit"
                    {...props}
                >
                    <Button.Icon name="stars" />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{t(keys.ai_suggestion_tooltip)}</Tooltip.Content>
        </Tooltip>
    );
};

const QuestionCardUploadButton: FC<Omit<ButtonProps, "children">> = (props) => {
    const { t, keys } = useQuestionTranslations();

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <Button
                    variant="outlined"
                    size="sm"
                    className="w-fit"
                    {...props}
                >
                    <Button.Icon name="document" />
                    {t(keys.show_upload_label)}
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{t(keys.show_upload_tooltip)}</Tooltip.Content>
        </Tooltip>
    );
};

type QuestionCardComponent = FC<FlexProps & { ref?: Ref<HTMLDivElement> }> & {
    Header: typeof QuestionCardHeader;
    StatusCol: typeof QuestionCardStatus;
    ProofWarning: typeof QuestionCardProofWarning;
    Content: typeof QuestionCardContent;
    Title: typeof QuestionCardTitle;
    Description: typeof QuestionCardDescription;
    SingleSelect: typeof SingleSelectSection;
    MultiSelect: typeof MultiSelectSection;
    QuestionCardGuidance: typeof QuestionCardGuidance;
    Switch: typeof QuestionCardSwitch;
    CopyButton: typeof QuestionCardCopyButton;
    NoteButton: typeof QuestionCardNoteButton;
    AiSuggestionButton: typeof QuestionCardAiSuggestionButton;
    UploadButton: typeof QuestionCardUploadButton;
    LockedStatus: typeof QuestionCardLockedStatus;
};

export const QuestionCard: QuestionCardComponent = ({ ref, ...props }) => {
    return (
        <FlexCol
            className="bg-primary border border-tertiary min-w-[400px] max-w-[832px]"
            gap="4"
            p="4"
            pb="5"
            br="lg"
            w="full"
            ref={ref}
            {...props}
        />
    );
};

QuestionCard.StatusCol = QuestionCardStatus;
QuestionCard.Header = QuestionCardHeader;
QuestionCard.ProofWarning = QuestionCardProofWarning;
QuestionCard.Content = QuestionCardContent;
QuestionCard.Title = QuestionCardTitle;
QuestionCard.Description = QuestionCardDescription;
QuestionCard.SingleSelect = SingleSelectSection;
QuestionCard.MultiSelect = MultiSelectSection;
QuestionCard.QuestionCardGuidance = QuestionCardGuidance;
QuestionCard.Switch = QuestionCardSwitch;
QuestionCard.CopyButton = QuestionCardCopyButton;
QuestionCard.NoteButton = QuestionCardNoteButton;
QuestionCard.AiSuggestionButton = QuestionCardAiSuggestionButton;
QuestionCard.UploadButton = QuestionCardUploadButton;
QuestionCard.LockedStatus = QuestionCardLockedStatus;
